import { FormEvent, useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { Input, Label, Textarea } from '@components/common/Forms';
import { bjToast, Button, TailwindSpinner } from '@components/common';
import { useBjForm } from '@hooks/useBjForm';
import { useSwrJobCombinedQuestions } from '@hooks/useSwrApi/endpoints/job-interview/useSwrJobCombinedQuestions';
import { useSwrJobSaveCombinedAnswers } from '@hooks/useSwrApi/endpoints/job-interview/useSwrJobSaveCombinedAnswers';

interface TextInterviewFormProps {
  jobSlug: string;
  locale: Locale;
  onSaveSuccess: () => void;
}

export const TextInterviewForm = (props: TextInterviewFormProps) => {
  const {
    jobSlug, locale, onSaveSuccess,
  } = props;

  const { t } = useTranslation('common');

  // Fetch combined questions (requirements + company text questions)
  const {
    data: combinedQuestions,
    isLoading: isFetchingQuestions,
    error: fetchError,
  } = useSwrJobCombinedQuestions({
    locale,
    jobSlug,
    shouldFetch: !!jobSlug,
  });

  const requirementQuestions = combinedQuestions?.requirements || [];
  const companyQuestions = combinedQuestions?.company?.flatMap((item) => item.questions || []) || [];

  // State for requirement answers: Map<questionId, boolean | undefined>
  const [requirementAnswers, setRequirementAnswers] = useState<Map<number, boolean | undefined>>(new Map());

  // Initialize requirement answers when questions load
  useEffect(() => {
    if (requirementQuestions.length > 0) {
      const initialAnswers = new Map<number, boolean | undefined>();
      requirementQuestions.forEach((question) => {
        if (question.questionId) {
          initialAnswers.set(question.questionId, undefined);
        }
      });
      setRequirementAnswers(initialAnswers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedQuestions]);

  const handleRequirementAnswerChange = (questionId: number, value: boolean) => {
    setRequirementAnswers((prev) => {
      const newAnswers = new Map(prev);
      newAnswers.set(questionId, value);
      return newAnswers;
    });
  };

  const {
    record, checkFormValidation,
  } = useBjForm({});

  const { trigger: saveCombinedAnswers, isMutating: isLoading } = useSwrJobSaveCombinedAnswers({
    locale,
    jobSlug,
    successCallback: () => {
      onSaveSuccess();
    },
    errorCallback: (error) => {
      bjToast.error(error.errors?.[0]?.message || t('global.error.generic'));
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate requirement answers
    if (requirementQuestions.length > 0) {
      const answeredCount = Array.from(requirementAnswers.values()).filter((v) => v !== undefined).length;
      if (answeredCount < requirementQuestions.length) {
        bjToast.error(t('apply.wizard.requirements.validation.all.required'));
        return;
      }
    }

    const formData = companyQuestions.length > 0 ? checkFormValidation(event) : {};
    if (companyQuestions.length > 0 && (!formData || typeof formData !== 'object')) {
      return;
    }

    // Build requirements payload
    const requirements = requirementQuestions.length > 0
      ? Array.from(requirementAnswers.entries())
        .filter(([, answerValue]) => answerValue !== undefined)
        .map(([questionId, answerValue]) => ({
          questionId,
          answerValue: answerValue!,
        }))
      : undefined;

    // Build company text answers payload
    const company = companyQuestions.length > 0
      ? companyQuestions.map((item, index) => {
        const textAnswerData = (formData as Record<string, { answerText: string; questionId: string }>)[`companyAnswers[${index}]`];
        return {
          questionId: Number(textAnswerData?.questionId || item.questionId),
          answerText: textAnswerData?.answerText || '',
        };
      })
      : undefined;

    void saveCombinedAnswers({ requirements, company });
  };

  // Auto-proceed if no questions
  useEffect(() => {
    if (combinedQuestions && requirementQuestions.length === 0 && companyQuestions.length === 0) {
      onSaveSuccess();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedQuestions]);

  // Loading state
  if (isFetchingQuestions) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <TailwindSpinner />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="text-center">
        <p className="text-error">{t('apply.wizard.requirements.fetch.error')}</p>
      </div>
    );
  }

  // Render the form
  // **************************
  return (
    <div>
      <p className="pb-6 text-lg">
        {t('apply.wizard.mini_interview.subtitle')}
      </p>
      <form noValidate onSubmit={onSubmit}>

        {/* Requirement questions (yes/no) */}
        {requirementQuestions.length > 0 && (
          <div className="mb-8 space-y-4">
            {requirementQuestions.map((question) => {
              if (!question.questionId) return null;
              const currentAnswer = requirementAnswers.get(question.questionId);

              return (
                <div key={question.questionId} className="rounded-lg border border-input p-4">
                  <p className="mb-3 font-medium text-ink-dark">{question.question}</p>
                  <div className="flex gap-4">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name={`requirement_${question.questionId}`}
                        checked={currentAnswer === true}
                        onChange={() => handleRequirementAnswerChange(question.questionId!, true)}
                        className="size-4 cursor-pointer accent-primary"
                      />
                      <span className="text-sm text-ink-medium">
                        {t('apply.wizard.requirements.yes')}
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name={`requirement_${question.questionId}`}
                        checked={currentAnswer === false}
                        onChange={() => handleRequirementAnswerChange(question.questionId!, false)}
                        className="size-4 cursor-pointer accent-primary"
                      />
                      <span className="text-sm text-ink-medium">
                        {t('apply.wizard.requirements.no')}
                      </span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Company text questions */}
        {companyQuestions.map((item, index) => (
          <div key={item.questionId} className="mb-8">
            <Label
              htmlFor={`companyAnswers[${index}].answerText`}
              description={item.question}
              descriptionClass="text-md font-medium"
              required
            >
              <Textarea
                minLength={3}
                required
                {...record(`companyAnswers[${index}].answerText`)}
                placeholder={t('global.placeholder.answer')}
              />
              <Input type="hidden" {...record(`companyAnswers[${index}].questionId`)} value={item.questionId} />
            </Label>
          </div>
        ))}

        <div className="flex flex-col sm:items-start md:items-center">
          <Button
            type="submit"
            styling="solid"
            color="secondary"
            size="md"
            className="mb-2 w-full rounded-full md:mb-6 md:w-auto"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t('global.action.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};
